import { useEffect, useState } from 'react'
import { deleteLead, fetchLeads, leadsCsvUrl, type LeadRow } from '../lib/api'
import { useDateRangeCtx } from './DateRangeContext'
import { DateRangeTabs } from './DateRangeTabs'
import { useFlowFilter } from './FlowFilterContext'
import { formatDateTime } from '../lib/format'
import type { Audience } from '../types'

const AUDIENCE_LABEL: Record<Audience, string> = { unge: 'Unge', aeldre: 'Ældre' }

// Backend now returns audience on each lead row; LeadRow type in lib/api.ts is
// being updated by the parallel agent. Read it via a structural type-guard.
function audienceOf(row: LeadRow): Audience | null {
  const a = (row as unknown as { audience?: string }).audience
  return a === 'unge' || a === 'aeldre' ? a : null
}

export function LeadsTable() {
  const { range } = useDateRangeCtx()
  const { variantParam, audienceParam } = useFlowFilter()
  const [page, setPage] = useState(1)
  const [data, setData] = useState<{ rows: LeadRow[]; total: number } | null>(null)
  const [selected, setSelected] = useState<LeadRow | null>(null)
  const [loading, setLoading] = useState(true)

  // Reset til side 1 når flow eller range ændres
  useEffect(() => { setPage(1) }, [variantParam, audienceParam, range.from, range.to])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchLeads(range, page, 25, variantParam, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => console.error('[leads]', e))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [range.from, range.to, page, variantParam, audienceParam])

  const totalPages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1

  const csvUrl = leadsCsvUrl(range, variantParam, audienceParam)

  return (
    <>
      <div className="section-toolbar">
        <DateRangeTabs />
        <a href={csvUrl} download style={{ textDecoration: 'none', marginLeft: 'auto' }}>
          <button className="time-tab">Eksportér CSV</button>
        </a>
      </div>

      <div className="section">
        <h2>Leads</h2>
        <p className="section-sub">{data?.total ?? '—'} i alt i denne periode</p>
        <table className="leads-table">
          <thead>
            <tr>
              <th>Tidspunkt</th>
              <th>Navn</th>
              <th>Telefon</th>
              <th>Postnr</th>
              <th>Målgruppe</th>
              <th>Variant</th>
              <th>Eksisterer?</th>
              <th>Zapier</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="empty-state">Indlæser…</td></tr>}
            {!loading && data && data.rows.length === 0 && (
              <tr><td colSpan={8} className="empty-state">Ingen leads i perioden.</td></tr>
            )}
            {data?.rows.map((row) => {
              const aud = audienceOf(row)
              return (
                <tr key={row.id} onClick={() => setSelected(row)}>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>{row.firstName} {row.lastName}</td>
                  <td>{row.phone}</td>
                  <td>{row.postnr ? `${row.postnr} ${row.by ?? ''}` : '—'}</td>
                  <td>{aud ? <span className={`pill audience-${aud}`}>{AUDIENCE_LABEL[aud]}</span> : <span className="muted small">—</span>}</td>
                  <td><span className={`pill ${row.variant}`}>{row.variant}</span></td>
                  <td>{row.hasExisting ? 'Ja' : 'Nej'}</td>
                  <td className={`status-${row.zapierStatus}`}>{row.zapierStatus}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="time-tab" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Forrige</button>
          <span className="muted" style={{ fontSize: 13 }}>Side {page} / {totalPages}</span>
          <button className="time-tab" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Næste</button>
        </div>
      </div>

      {selected && (
        <LeadModal
          lead={selected}
          onClose={() => setSelected(null)}
          onDelete={async () => {
            if (!confirm('Slet denne lead permanent? (GDPR right to erasure)')) return
            await deleteLead(selected.id)
            setSelected(null)
            setData(null)
            fetchLeads(range, page, 25, variantParam, audienceParam)
              .then(setData).catch(() => {})
          }}
        />
      )}
    </>
  )
}

function LeadModal({ lead, onClose, onDelete }: { lead: LeadRow; onClose: () => void; onDelete: () => void }) {
  return (
    <div className="lead-modal" onClick={onClose}>
      <div className="card" onClick={(e) => e.stopPropagation()}>
        <h3>{lead.firstName} {lead.lastName}</h3>
        <dl>
          <dt>Tidspunkt</dt><dd>{formatDateTime(lead.createdAt)}</dd>
          <dt>Telefon</dt><dd>{lead.phone}</dd>
          <dt>Postnr / by</dt><dd>{lead.postnr ?? '—'} {lead.by ?? ''}</dd>
          <dt>Alder</dt><dd>{lead.age ?? '—'}</dd>
          <dt>Forsikringer</dt><dd>{prettyInsurances(lead.insurances)}</dd>
          <dt>Målgruppe</dt><dd>{(() => {
            const a = audienceOf(lead)
            return a ? AUDIENCE_LABEL[a] : '—'
          })()}</dd>
          <dt>Variant</dt><dd>{lead.variant}</dd>
          <dt>Eksisterende kunde</dt><dd>{lead.hasExisting ? 'Ja' : 'Nej'}</dd>
          <dt>Marketing samtykke</dt><dd>{lead.consentMarketing ? 'Ja' : 'Nej'}</dd>
          <dt>Zapier status</dt><dd>{lead.zapierStatus}</dd>
        </dl>
        <div className="actions">
          <button className="danger" onClick={onDelete}>Slet (GDPR)</button>
          <button onClick={onClose}>Luk</button>
        </div>
      </div>
    </div>
  )
}

function prettyInsurances(json: string | null): string {
  if (!json) return '—'
  try {
    const arr = JSON.parse(json) as string[]
    return arr.length === 0 ? '—' : arr.join(', ')
  } catch {
    return json
  }
}
